<script setup lang="ts">
import { computed } from 'vue'
import type { UsageWeek } from '../stores/usage'
import {
  cacheHitRate,
  formatTokens,
  formatUsd,
  modelColor,
  shortModel,
  totalTokens,
} from '../composables/usageFormatting'

// The weekly-spend half of UsageView.vue (#1090) — the "this week so
// far" callout, its per-operation cost breakdown, and the historical
// per-week stacked-bar rows. Extracted so the parent stays under the
// 300-line hard limit; no behavior change from the inline version.
const props = defineProps<{
  currentWeek: UsageWeek | null
  pastWeeks: UsageWeek[]
}>()

// Max cost across the past weeks, used to scale the bar widths
// proportionally. A zero-cost dataset means no bars render.
const maxWeekCost = computed(() => {
  return Math.max(0, ...props.pastWeeks.map((w) => w.total_cost_cents))
})

function weekCostRatio(week: UsageWeek): number {
  if (maxWeekCost.value === 0) return 0
  return week.total_cost_cents / maxWeekCost.value
}

function formatWeekStart(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}
</script>

<template>
  <!-- "This week so far" callout — pinned at the top, computed live. -->
  <section
    v-if="currentWeek"
    data-testid="this-week-so-far"
    class="rounded border border-white/10 bg-white/[0.02] p-4 flex flex-col gap-3"
  >
    <div class="flex items-baseline justify-between gap-3">
      <h2 class="text-sm font-medium text-white/80 m-0">This week so far</h2>
      <span class="text-xs text-white/50"> week of {{ formatWeekStart(currentWeek.week_start) }} </span>
    </div>
    <div class="flex items-baseline gap-3">
      <span class="text-3xl font-semibold text-white tabular-nums">
        {{ formatUsd(currentWeek.total_cost_cents) }}
      </span>
      <span class="text-xs text-white/40">
        across {{ currentWeek.models.length }} model{{ currentWeek.models.length === 1 ? '' : 's' }}
      </span>
    </div>
    <div v-if="currentWeek.models.length > 0" class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60">
      <span v-for="m in currentWeek.models" :key="m.model" class="flex items-center gap-1.5">
        <span class="inline-block w-2 h-2 rounded-sm" :class="modelColor(m.model)" />
        {{ shortModel(m.model) }}
        <span class="text-white/40">
          · {{ formatTokens(totalTokens(m)) }} tokens · {{ formatUsd(m.estimated_cost_cents) }}
        </span>
        <span
          v-if="cacheHitRate(m) !== null"
          data-testid="cache-hit-rate"
          class="text-emerald-300/70"
          title="Prompt-cache hit rate — share of input served from cache"
          >· {{ cacheHitRate(m) }}% cached</span
        >
      </span>
    </div>

    <!-- Per-operation cost breakdown: which feature drives
         spend. Current-week only — the backend derives it live. -->
    <div
      v-if="currentWeek.operations && currentWeek.operations.length > 0"
      data-testid="operation-breakdown"
      class="flex flex-col gap-1 border-t border-white/10 pt-2"
    >
      <span class="text-xs text-white/40">By operation</span>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60">
        <span
          v-for="op in currentWeek.operations"
          :key="op.operation"
          data-testid="operation-row"
          class="flex items-center gap-1.5 tabular-nums"
        >
          <span class="capitalize text-white/80">{{ op.operation }}</span>
          <span class="text-white/40">
            · {{ formatTokens(totalTokens(op)) }} tokens · {{ formatUsd(op.estimated_cost_cents) }}
          </span>
        </span>
      </div>
    </div>
  </section>

  <!-- Historical weeks (frozen UsageReport snapshots). -->
  <section v-if="pastWeeks.length > 0" data-testid="past-weeks" class="flex flex-col gap-3">
    <h2 class="text-sm font-medium text-white/80 m-0">Previous weeks</h2>
    <p class="sr-only">
      Weekly cost trend — one row per week with a cost bar drawn proportional to the highest-cost week; each bar segment
      is a model's share, also listed as text below it.
    </p>

    <div
      v-for="week in pastWeeks"
      :key="week.week_start"
      data-testid="usage-week-row"
      class="rounded border border-white/10 bg-white/[0.02] p-3 flex flex-col gap-2"
    >
      <div class="flex items-baseline justify-between gap-3">
        <span class="text-sm text-white/80 tabular-nums"> Week of {{ formatWeekStart(week.week_start) }} </span>
        <span class="text-sm text-white font-medium tabular-nums">
          {{ formatUsd(week.total_cost_cents) }}
        </span>
      </div>

      <!-- Stacked bar: width proportional to this week's share of the highest-cost week. -->
      <div
        class="flex h-2 rounded-sm overflow-hidden bg-white/5"
        :style="{ width: `${Math.max(weekCostRatio(week) * 100, 0)}%` }"
      >
        <div
          v-for="m in week.models"
          :key="m.model"
          :class="modelColor(m.model)"
          :style="{ flexBasis: `${(m.estimated_cost_cents / week.total_cost_cents) * 100}%` }"
          :title="`${shortModel(m.model)}: ${formatUsd(m.estimated_cost_cents)}`"
        />
      </div>

      <div v-if="week.models.length > 0" class="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/50">
        <span v-for="m in week.models" :key="m.model" class="flex items-center gap-1">
          <span class="inline-block w-2 h-2 rounded-sm" :class="modelColor(m.model)" />
          {{ shortModel(m.model) }}
          <span class="text-white/30">· {{ formatUsd(m.estimated_cost_cents) }}</span>
        </span>
      </div>
    </div>
  </section>
</template>
